import React, { useState, useEffect } from "react";
import axios from "../../utils/axios";
import Topbar from "../../components/Topbar";
import Sidebar from "../../components/Sidebar";
import { getUser } from "../../utils/Common";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Editor } from '@tinymce/tinymce-react';
import "react-toastify/dist/ReactToastify.css";

const HotelConfig = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [isMiniSidebar, setMiniSidebar] = useState(false);
  const { hotel_id } = useParams();
  const [isLoading, setLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [propData, setPropData] = useState({});
  const [formData, setFormData] = useState({});
  const [logoFile, setLogoFile] = useState(null); // State for handling logo upload
  const [logoPreview, setLogoPreview] = useState(null); // State for previewing the logo

  // Fetch hotel data
  const getDetailProperty = async (hotel_id) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/detail/hotel/${hotel_id}`);
      if (response.status === 200) {
        setPropData(response.data);
        setFormData(response.data); // Populate form with fetched data
      } else {
        throw new Error("Failed to fetch hotel details.");
      }
    } catch (err) {
      console.error("Error fetching property details:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle file change for logo upload with preview
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file)); // Generate a temporary URL for preview
    }
  };

  // Handle form submission for other fields
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    try {
      const response = await axios.post(`/api/update/hotel/setting`, formDataToSend);
      if (response.status === 200) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("Error updating hotel configuration:", err);
      toast.error("An error occurred. Please try again.");
    }
  };

  // Handle separate logo upload submission
  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error("Please select a logo file before uploading.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("LOGO_HOTEL", logoFile);
    formDataToSend.append("ID_HOTEL", hotel_id); // Include the hotel ID for association

    try {
      const response = await axios.post(`/api/upload/hotel/logo`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        toast.success("Logo uploaded successfully!");
        console.log(response.data)
      } else {
        toast.error("Failed to upload logo.");
      }
    } catch (err) {
      console.error("Error uploading logo:", err);
      toast.error("An error occurred while uploading the logo.");
    }
  };

  useEffect(() => {
    const userSession = getUser(); // Call getUser to fetch user data from localStorage
        if (userSession) {
          setUserData(userSession); // Store user data in the state if it exists
      getDetailProperty(hotel_id);
    } else {
      navigate('/'); // Optionally clear the user data if not found
    }
  }, [hotel_id, navigate]);

  return (
    <div className="flex h-screen bg-gray-200">
      {/* Sidebar */}
      <Sidebar
        setMiniSidebar={setMiniSidebar}
        isMiniSidebar={isMiniSidebar}
        propertyID={propData.ID_HOTEL || "Loading..."}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar 
          setMiniSidebar={setMiniSidebar} 
          isMiniSidebar={isMiniSidebar} 
        />

        {/* Main Section */}
        <main className="flex-1 p-5 overflow-y-auto bg-gray-100">
          {/* Breadcrumb */}
          <div className="mb-4 text-sm text-[#101827]">
             <span>Configuration</span> &gt; <span>{propData.NAME_HOTEL}</span>
          </div>

          {/* Form Section */}
          <section className="bg-white rounded shadow-md py-4 px-6 text-xs text-[#101827]">
            

            {isLoading ? (
              <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0f1827] mb-4"></div>
                  <p className="text-gray-700">Load Data...</p>
                </div>
            </div>
            ) : (
              <>
              <h6 className="text-base font-bold mb-4">Configuration</h6>
                {/* Separate Logo Upload Section */}
                <div className="w-full mb-8">
                 
                  <div className="flex items-center gap-4">
                    <img
                      src={logoPreview || formData.LOGO_HOTEL} // Use preview if available, fallback to default logo
                      className="w-24 h-24 rounded-full border-2 border-gray-300 shadow-lg object-scale-down"
                      alt="Hotel Logo"
                    />
                    <input
                      type="file"
                      name="LOGO_HOTEL"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full border rounded px-3 py-2 cover-"
                    />
                  </div>

                  {logoFile && (
                    <div className="text-right mt-4">
                      <button
                        type="button"
                        onClick={handleLogoUpload}
                        className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded"
                      >
                        Upload Logo
                      </button>
                    </div>
                  )}
                </div>

                {/* Main Configuration Form */}
                <form onSubmit={handleSubmit} className="flex flex-wrap gap-6">
                  {/* Left Column */}
                  <div className="flex-1 min-w-[300px]">
                    {[
                      { label: "Property Name", name: "NAME_HOTEL", value: formData.NAME_HOTEL },
                      { label: "URL", name: "URL", value: formData.URL },
                      { label: "Phone", name: "PHONE", value: formData.PHONE },
                      { label: "Mobile Phone", name: "ALT_PHONE", value: formData.ALT_PHONE },
                      { label: "Province", name: "PROV", value: formData.PROV },
                      { label: "Postal Code", name: "POSTCODE", value: formData.POSTCODE },
                      { label: "State", name: "STATE", value: formData.STATE },
                      
                    ].map((field, index) => (
                      <div key={index} className="mb-4">
                        <label className="block text-xs font-medium mb-1">{field.label}</label>
                        <input
                          type="text"
                          name={field.name}
                          value={field.value || ""}
                          onChange={handleChange}
                          className="w-full border rounded px-3 py-2"
                          required
                        />
                      </div>
                    ))}
                  </div>

                  {/* Right Column */}
                  <div className="flex-1 min-w-[300px]">
                  {[
                      { label: "Email", name: "EMAIL_CC", value: formData.EMAIL_CC },
                      { label: "Address", name: "ADDRESS1", value: formData.ADDRESS1 },
                      { label: "Official Website", name: "WEB", value: formData.WEB },
                      { label: "Facebook", name: "fb", value: formData.fb },
                      { label: "Instagram", name: "ig", value: formData.ig },
                      { label: "TikTok", name: "tiktok", value: formData.tiktok },
                      { label: "Tripadvisor", name: "tripadvisor", value: formData.tripadvisor },
                    ].map((field, index) => (
                      <div key={index} className="mb-4">
                        <label className="block text-xs font-medium mb-1">{field.label}</label>
                        <input
                          type="text"
                          name={field.name}
                          value={field.value || ""}
                          onChange={handleChange}
                          className="w-full border rounded px-3 py-2"
                          required
                        />
                      </div>
                    ))}
                  </div>
                  <hr/>

                  {/* Full Width Fields */}
                  <div className="w-full">
                    {/* Textareas with TinyMCE */}
                    {[
                      { label: "Terms & Condition", name: "TERMS_CONDITION", value: formData.TERMS_CONDITION },
                      { label: "Cancel & Policy", name: "CANCEL_POLICY", value: formData.CANCEL_POLICY },
                    ].map((field, index) => (
                      <div key={index} className="mb-4">
                        <label className="block text-xs font-medium mb-1">{field.label}</label>
                        <Editor
                          name={field.name}
                          value={field.value || ""}
                          apiKey='jzxha84y3xyify9uwj6ec47iceogua3cg0k4ftz1bfvxgbr5'
                          init={{
                            height: 500, // Adjust height as necessary
                            menubar: false,
                            plugins: [
                              "advlist autolink lists link image charmap print preview anchor",
                              "searchreplace visualblocks code fullscreen",
                              "insertdatetime media table paste code help wordcount"
                            ],
                            toolbar:
                              "undo redo | formatselect | bold italic backcolor | \
                              alignleft aligncenter alignright alignjustify | \
                              bullist numlist outdent indent | removeformat | help",
                            content_style: "body { font-family:Arial, sans-serif; font-size:14px; }"
                          }}
                          onEditorChange={(content, editor) => handleChange({ target: { name: field.name, value: content } })}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Submit Button */}
                  <div className="w-full text-right">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
                    >
                      Update Configuration
                    </button>
                  </div>
                </form>
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default HotelConfig;
